import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { womensHealthApi } from '../../../lib/api';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, CheckCircle, Clock, FileText,
  Eye, Stethoscope, Flag, ClipboardCheck, Calendar, Shield,
  TrendingUp, Image as ImageIcon, Activity
} from 'lucide-react';

const SEVERITY_COLORS = {
  low: 'bg-blue-100 text-blue-700 border-blue-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  urgent: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_MAP = {
  draft: { label: 'Taslak', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'Devam Ediyor', color: 'bg-blue-100 text-blue-700' },
  awaiting_review: { label: 'Degerlendirme Bekliyor', color: 'bg-amber-100 text-amber-700' },
  reviewed: { label: 'Degerlendirildi', color: 'bg-green-100 text-green-700' },
  closed: { label: 'Kapandi', color: 'bg-slate-200 text-slate-500' },
};

export default function EncounterDetailPage() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  // Review form
  const [reviewForm, setReviewForm] = useState({
    clinical_impression: '', recommendation: '',
    needs_lab_testing: false, needs_specialist_review: false,
    urgency: 'routine', review_type: 'general',
  });

  // Followup form
  const [followupForm, setFollowupForm] = useState({
    followup_type: 'recheck', due_date: '', instructions: '',
  });

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const data = await womensHealthApi.getEncounterDetail(encounterId);
      setDetail(data);
      if (data.clinician_review) {
        setReviewForm({
          clinical_impression: data.clinician_review.clinical_impression || '',
          recommendation: data.clinician_review.recommendation || '',
          needs_lab_testing: data.clinician_review.needs_lab_testing || false,
          needs_specialist_review: data.clinician_review.needs_specialist_review || false,
          urgency: data.clinician_review.urgency || 'routine',
          review_type: data.clinician_review.review_type || 'general',
        });
      }
    } catch (err) {
      toast.error('Vaka yuklenemedi');
      navigate('/doctor/womens-health');
    } finally { setLoading(false); }
  }, [encounterId, navigate]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSaveReview = async () => {
    setSaving(true);
    try {
      await womensHealthApi.saveReview(encounterId, { ...reviewForm, encounter_id: encounterId });
      toast.success('Degerlendirme kaydedildi');
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Kayit hatasi');
    } finally { setSaving(false); }
  };

  const handleFinalizeReview = async () => {
    setSaving(true);
    try {
      await womensHealthApi.finalizeReview(encounterId);
      toast.success('Degerlendirme tamamlandi');
      fetchDetail();
    } catch (err) {
      toast.error(err.message || 'Hata');
    } finally { setSaving(false); }
  };

  const handleResolveFlag = async (flagId) => {
    try {
      await womensHealthApi.resolveFlag(flagId);
      toast.success('Bayrak cozumlendi');
      fetchDetail();
    } catch (err) { toast.error('Hata'); }
  };

  const handleCreateFollowup = async () => {
    setSaving(true);
    try {
      await womensHealthApi.createFollowup(encounterId, { ...followupForm, encounter_id: encounterId });
      toast.success('Takip plani olusturuldu');
      setFollowupForm({ followup_type: 'recheck', due_date: '', instructions: '' });
      fetchDetail();
    } catch (err) { toast.error('Hata'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <DashboardLayout title="Vaka Detayi">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!detail) return null;

  const { encounter, consents, symptom_intake, triage_flags, clinician_review, followup_plans, images } = detail;
  const statusInfo = STATUS_MAP[encounter.status] || STATUS_MAP.draft;
  const unresolvedFlags = (triage_flags || []).filter(f => !f.resolved_at);
  const urgentFlags = unresolvedFlags.filter(f => f.severity === 'urgent' || f.severity === 'high');

  return (
    <DashboardLayout title={encounter.patient_name || 'Vaka'} subtitle={`${encounter.encounter_type} - ${new Date(encounter.created_at).toLocaleDateString('tr-TR')}`}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/womens-health')} data-testid="wh-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              {urgentFlags.length > 0 && (
                <Badge className="ml-2 bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3 mr-1" />{urgentFlags.length} Acil</Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400">Vaka ID: {encounter.id.slice(0, 8)}</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="overview" data-testid="wh-tab-overview"><Activity className="w-3.5 h-3.5 mr-1" /> Ozet</TabsTrigger>
            <TabsTrigger value="symptoms" data-testid="wh-tab-symptoms"><Stethoscope className="w-3.5 h-3.5 mr-1" /> Semptomlar</TabsTrigger>
            <TabsTrigger value="flags" data-testid="wh-tab-flags"><Flag className="w-3.5 h-3.5 mr-1" /> Bayraklar ({unresolvedFlags.length})</TabsTrigger>
            <TabsTrigger value="review" data-testid="wh-tab-review"><ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Degerlendirme</TabsTrigger>
            <TabsTrigger value="followup" data-testid="wh-tab-followup"><Calendar className="w-3.5 h-3.5 mr-1" /> Takip</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-0 shadow">
                <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm">Vaka Bilgileri</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Hasta</span><span className="font-medium">{encounter.patient_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Sikayet</span><span>{encounter.chief_complaint || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tur</span><span>{encounter.encounter_type}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Gebe</span><span>{encounter.pregnancy_status ? 'Evet' : 'Hayir'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tarih</span><span>{new Date(encounter.created_at).toLocaleString('tr-TR')}</span></div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow">
                <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-green-600" /> Onam Durumu</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-2 text-sm">
                  {(consents || []).length > 0 ? consents.map(c => (
                    <div key={c.id} className="flex items-center justify-between">
                      <span className="capitalize">{c.consent_type.replace('_', ' ')}</span>
                      <Badge className="bg-green-100 text-green-700 text-[10px]"><CheckCircle className="w-3 h-3 mr-1" /> Onaylandi</Badge>
                    </div>
                  )) : <p className="text-slate-400">Onam bilgisi yok</p>}
                </CardContent>
              </Card>
              {(images || []).length > 0 && (
                <Card className="border-0 shadow lg:col-span-2">
                  <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Goruntular ({images.length})</CardTitle></CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {images.map(img => (
                        <div key={img.id} className="aspect-square rounded-lg bg-slate-100 border flex items-center justify-center overflow-hidden">
                          <ImageIcon className="w-8 h-8 text-slate-300" />
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-2">Goruntuler guvenli depolamada saklanmaktadir. Hasta onamiyla erisim saglanir.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* SYMPTOMS TAB */}
          <TabsContent value="symptoms">
            <Card className="border-0 shadow">
              <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm">Semptom Ozeti</CardTitle></CardHeader>
              <CardContent className="p-4">
                {symptom_intake ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {[
                        { key: 'itching', label: 'Kasinma' },
                        { key: 'burning', label: 'Yanma' },
                        { key: 'pain', label: 'Agri' },
                        { key: 'dysuria', label: 'Dizuri' },
                        { key: 'dyspareunia', label: 'Disparoni' },
                        { key: 'pelvic_pain', label: 'Pelvik Agri' },
                        { key: 'bleeding', label: 'Kanama' },
                        { key: 'fever', label: 'Ates' },
                        { key: 'new_partner', label: 'Yeni Partner' },
                        { key: 'sti_risk_noted', label: 'STI Risk' },
                      ].map(s => (
                        <div key={s.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${symptom_intake[s.key] ? 'bg-red-50 text-red-700 font-medium' : 'bg-slate-50 text-slate-400'}`}>
                          {symptom_intake[s.key] ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
                          {s.label}
                        </div>
                      ))}
                    </div>
                    {symptom_intake.discharge_present && (
                      <Card className="border">
                        <CardContent className="p-3 text-sm space-y-1">
                          <p className="font-medium">Akinti Detaylari</p>
                          <div className="grid grid-cols-3 gap-2 text-slate-600">
                            <div>Renk: <span className="font-medium">{symptom_intake.discharge_color || '-'}</span></div>
                            <div>Kivam: <span className="font-medium">{symptom_intake.discharge_consistency || '-'}</span></div>
                            <div>Miktar: <span className="font-medium">{symptom_intake.discharge_amount || '-'}</span></div>
                          </div>
                          {symptom_intake.odor_present && <div>Koku: <span className="font-medium">{symptom_intake.odor_type || 'Var'}</span></div>}
                        </CardContent>
                      </Card>
                    )}
                    {symptom_intake.duration_days && <p className="text-sm text-slate-500">Sure: {symptom_intake.duration_days} gun</p>}
                  </div>
                ) : <p className="text-slate-400 py-8 text-center">Semptom formu henuz doldurulmadi</p>}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FLAGS TAB */}
          <TabsContent value="flags">
            <Card className="border-0 shadow">
              <CardHeader className="py-3 px-4 bg-slate-50 border-b">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Flag className="w-4 h-4 text-amber-500" /> Triaj Bayraklari
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {(triage_flags || []).length > 0 ? (
                  <div className="space-y-3">
                    {triage_flags.map(flag => (
                      <div key={flag.id} className={`flex items-start gap-3 p-3 rounded-lg border ${flag.resolved_at ? 'bg-slate-50 border-slate-200 opacity-60' : SEVERITY_COLORS[flag.severity]}`}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge variant="outline" className="text-[10px]">{flag.flag_code}</Badge>
                            <Badge className={`text-[10px] ${SEVERITY_COLORS[flag.severity]}`}>{flag.severity.toUpperCase()}</Badge>
                          </div>
                          <p className="text-sm">{flag.flag_reason}</p>
                          <p className="text-[10px] text-slate-400 mt-1">Kaynak: {flag.trigger_source} | {new Date(flag.created_at).toLocaleString('tr-TR')}</p>
                        </div>
                        {!flag.resolved_at && (
                          <Button variant="outline" size="sm" onClick={() => handleResolveFlag(flag.id)} data-testid={`wh-resolve-flag-${flag.id}`}>
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Cozumle
                          </Button>
                        )}
                        {flag.resolved_at && <Badge className="bg-green-100 text-green-700 text-[10px]">Cozumlendi</Badge>}
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400 italic mt-2">
                      Bu bayraklar AI destekli klinik destek amaciyla olusturulmustur. Kesin tani niteligi tasimaz, klinik korelasyon gerektirir.
                    </p>
                  </div>
                ) : <p className="text-slate-400 py-8 text-center">Triaj bayragi olusturulmadi</p>}
              </CardContent>
            </Card>
          </TabsContent>

          {/* REVIEW TAB */}
          <TabsContent value="review">
            <Card className="border-0 shadow">
              <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm">Klinisyen Degerlendirmesi</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-4">
                {clinician_review?.finalized_at ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                    <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Tamamlandi</Badge>
                    <div className="text-sm space-y-1">
                      <p><span className="text-slate-500">Izlenim:</span> {clinician_review.clinical_impression}</p>
                      <p><span className="text-slate-500">Oneri:</span> {clinician_review.recommendation}</p>
                      <p><span className="text-slate-500">Aciliyet:</span> {clinician_review.urgency}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-xs">Klinik Izlenim</Label>
                      <Textarea
                        value={reviewForm.clinical_impression}
                        onChange={(e) => setReviewForm(p => ({ ...p, clinical_impression: e.target.value }))}
                        placeholder="Klinik izleniminizi yazin... (ornek: patern X'e benziyor, klinik korelasyon gerektirir)"
                        rows={3}
                        data-testid="wh-review-impression"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Oneri</Label>
                      <Textarea
                        value={reviewForm.recommendation}
                        onChange={(e) => setReviewForm(p => ({ ...p, recommendation: e.target.value }))}
                        placeholder="Onerilerinizi yazin..."
                        rows={2}
                        data-testid="wh-review-recommendation"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Aciliyet</Label>
                        <Select value={reviewForm.urgency} onValueChange={(v) => setReviewForm(p => ({ ...p, urgency: v }))}>
                          <SelectTrigger data-testid="wh-review-urgency"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="routine">Rutin</SelectItem>
                            <SelectItem value="soon">Yakin Zamanda</SelectItem>
                            <SelectItem value="urgent">Acil</SelectItem>
                            <SelectItem value="emergency">Acil Servis</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <Checkbox checked={reviewForm.needs_lab_testing} onCheckedChange={(v) => setReviewForm(p => ({ ...p, needs_lab_testing: !!v }))} />
                        <Label className="text-xs">Lab Testi Gerekli</Label>
                      </div>
                      <div className="flex items-center gap-2 pt-5">
                        <Checkbox checked={reviewForm.needs_specialist_review} onCheckedChange={(v) => setReviewForm(p => ({ ...p, needs_specialist_review: !!v }))} />
                        <Label className="text-xs">Uzman Yonlendirmesi</Label>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleSaveReview} disabled={saving} variant="outline" data-testid="wh-save-review">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Taslak Kaydet'}
                      </Button>
                      <Button onClick={handleFinalizeReview} disabled={saving || !reviewForm.clinical_impression} className="bg-green-600 hover:bg-green-700" data-testid="wh-finalize-review">
                        <CheckCircle className="w-4 h-4 mr-1" /> Degerlendirmeyi Tamamla
                      </Button>
                    </div>
                    <p className="text-[10px] text-slate-400 italic">
                      Bu bir AI destekli klinik karar destek sistemidir. Ciktilar kesin tani degil, klinisyen degerlendirmesine destek amaciyla sunulmustur.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FOLLOWUP TAB */}
          <TabsContent value="followup">
            <div className="space-y-4">
              {(followup_plans || []).length > 0 && (
                <Card className="border-0 shadow">
                  <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm">Mevcut Takip Planlari</CardTitle></CardHeader>
                  <CardContent className="p-4 space-y-2">
                    {followup_plans.map(fp => (
                      <div key={fp.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border text-sm">
                        <div>
                          <p className="font-medium capitalize">{fp.followup_type.replace('_', ' ')}</p>
                          {fp.due_date && <p className="text-xs text-slate-500">Tarih: {fp.due_date}</p>}
                          {fp.instructions && <p className="text-xs text-slate-400">{fp.instructions}</p>}
                        </div>
                        <Badge variant="outline" className="text-[10px]">{fp.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              <Card className="border-0 shadow">
                <CardHeader className="py-3 px-4 bg-slate-50 border-b"><CardTitle className="text-sm">Yeni Takip Plani</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Takip Turu</Label>
                      <Select value={followupForm.followup_type} onValueChange={(v) => setFollowupForm(p => ({ ...p, followup_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="recheck">Kontrol</SelectItem>
                          <SelectItem value="lab_result">Lab Sonucu</SelectItem>
                          <SelectItem value="specialist">Uzman Randevusu</SelectItem>
                          <SelectItem value="imaging">Goruntuleme</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tarih</Label>
                      <Input type="date" value={followupForm.due_date} onChange={(e) => setFollowupForm(p => ({ ...p, due_date: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Talimatlar</Label>
                    <Textarea
                      value={followupForm.instructions}
                      onChange={(e) => setFollowupForm(p => ({ ...p, instructions: e.target.value }))}
                      placeholder="Takip talimatlari..."
                      rows={2}
                    />
                  </div>
                  <Button onClick={handleCreateFollowup} disabled={saving} className="bg-[#5A67D8]" data-testid="wh-create-followup">
                    <Calendar className="w-4 h-4 mr-1" /> Takip Plani Olustur
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
